import { DateRange } from "~/document";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "~/lib/utils";
import { formatDateReadable } from "~/util";
import type { DateRange as RdpDateRange } from "react-day-picker";

type CommonProps = {
  pickerProps?: any;
  helperText: string;
  disabled?: boolean;
};

interface SingleProps extends CommonProps {
  value: Date | undefined;
  allowPast: boolean | undefined;
  onChange: (a: Date | undefined) => void;
  labelText?: string;
}

interface RangedProps extends CommonProps {
  value: DateRange | undefined;
  onChange: (a: DateRange | undefined) => void;
  withDefaultValue?: boolean;
  allowPast?: boolean;
}

const getTodayDate = (): Date => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getYesterdayDate = (): Date => {
  const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
  return yesterday;
};

const getTodayRange = (): DateRange => {
  return [getTodayDate(), getTodayDate()];
};

const getYesterdayRange = (): DateRange => {
  return [getYesterdayDate(), getYesterdayDate()];
};

const getLastWeekDateRange = (): DateRange => {
  const lastWeekDay = new Date(new Date().setDate(new Date().getDate() - 7));
  return [lastWeekDay, getTodayDate()];
};

const getLastMonthDateRange = (): DateRange => {
  const lastMonthDay = new Date(new Date().setMonth(new Date().getMonth() - 1));
  return [lastMonthDay, getTodayDate()];
};

const getLastYearDateRange = (): DateRange => {
  const beg = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  return [beg, getTodayDate()];
};

const getDateRangeUntilToday = (): DateRange => {
  return [null, getYesterdayDate()];
};

// Year range offered by the month/year dropdowns.
const dropdownStart = new Date(1970, 0);
const dropdownEnd = () => new Date(new Date().getFullYear() + 10, 11);

const Shortcut = (props: { onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={props.onClick}
    className="cursor-pointer text-left text-sm text-primary-600 hover:underline"
  >
    {props.children}
  </button>
);

const ClearButton = (props: { label: string; onClear: () => void }) => (
  <span
    role="button"
    tabIndex={-1}
    aria-label={props.label}
    className="ml-auto inline-flex shrink-0"
    onPointerDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
    }}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      props.onClear();
    }}
  >
    <X className="size-4 opacity-60 hover:opacity-100" />
  </span>
);

export const SingleDatePicker = (props: SingleProps) => {
  const { value, onChange, allowPast = false, labelText = "", disabled, helperText } = props;
  const [open, setOpen] = useState(false);

  return (
    <div>
      {!!labelText && <label className="mb-1.5 block text-sm font-semibold">{labelText}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn("w-full justify-start gap-2 font-normal", !value && "text-muted-foreground")}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            <span className="flex-1 text-left">{value ? formatDateReadable(value) : "Select date"}</span>
            {value && !disabled && <ClearButton label="Clear date" onClear={() => onChange(undefined)} />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[10000] w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            onSelect={(d) => {
              onChange(d ?? undefined);
              setOpen(false);
            }}
            captionLayout="dropdown"
            startMonth={dropdownStart}
            endMonth={dropdownEnd()}
            disabled={allowPast ? undefined : { before: getTodayDate() }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
      {!!helperText && <div className="mt-1 text-xs text-muted-foreground">{helperText}</div>}
    </div>
  );
};

export const RangedDatePicker = (props: RangedProps) => {
  const { value, onChange, withDefaultValue = true, allowPast, disabled, helperText } = props;
  const [open, setOpen] = useState(false);

  const from = value?.[0] ?? (withDefaultValue ? new Date(0) : undefined);
  const to = value?.[1] ?? (withDefaultValue ? getTodayDate() : undefined);
  const selected: RdpDateRange | undefined = from || to ? { from: from ?? undefined, to: to ?? undefined } : undefined;

  const hasValue = !!(value && (value[0] || value[1]));
  const triggerLabel = hasValue
    ? `${value![0] ? formatDateReadable(value![0]!) : "…"} → ${value![1] ? formatDateReadable(value![1]!) : "…"}`
    : "Select date range";

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn("w-full justify-start gap-2 font-normal", !hasValue && "text-muted-foreground")}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            <span className="flex-1 truncate text-left">{triggerLabel}</span>
            {hasValue && !disabled && <ClearButton label="Clear range" onClear={() => onChange(undefined)} />}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[10000] w-auto p-0" align="start">
          <div className="flex flex-col gap-3 p-3 sm:flex-row">
            <div className="flex flex-row flex-wrap gap-x-3 gap-y-1.5 sm:w-28 sm:flex-col sm:flex-nowrap">
              <span className="text-xs font-semibold text-muted-foreground">Set to:</span>
              <Shortcut onClick={() => onChange(getTodayRange())}>Today</Shortcut>
              <Shortcut onClick={() => onChange(getYesterdayRange())}>Yesterday</Shortcut>
              <Shortcut onClick={() => onChange(getLastWeekDateRange())}>Last 7 days</Shortcut>
              <Shortcut onClick={() => onChange(getLastMonthDateRange())}>Last 30 days</Shortcut>
              <Shortcut onClick={() => onChange(getLastYearDateRange())}>This year</Shortcut>
              <Shortcut onClick={() => onChange(getDateRangeUntilToday())}>Until today</Shortcut>
            </div>
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={selected}
              defaultMonth={from ?? undefined}
              onSelect={(r: RdpDateRange | undefined) => onChange([r?.from ?? null, r?.to ?? null])}
              captionLayout="dropdown"
              startMonth={dropdownStart}
              endMonth={dropdownEnd()}
              disabled={allowPast ? undefined : { before: getTodayDate() }}
              autoFocus
            />
          </div>
        </PopoverContent>
      </Popover>
      {!!helperText && <div className="mt-1 text-xs text-muted-foreground">{helperText}</div>}
    </div>
  );
};
